import { PayloadAction, createSlice, createSelector } from "@reduxjs/toolkit"
import { RootState } from "../store"
import { subscriptionsApi } from "../queries/subscriptions"
import { leadsApi } from "../queries/lead"
import { TabRangeState, EngagementsTab, TabDateFilter } from "../../types"
import { DateRangePreset } from "../../common/ui/activity/date-filter"

export const engagementsSliceName = "engagements"

export interface EngagementsState {
  tabs: {
    range: TabRangeState
  }
  counts: {
    engagements: number | null
    leads: number | null
  }
}

// Calculate 120 days ago from now
const now = new Date()
const oneHundredTwentyDaysAgo = new Date(now)
oneHundredTwentyDaysAgo.setDate(now.getDate() - 120)
oneHundredTwentyDaysAgo.setHours(0, 0, 0, 0) // Start of day

// Helper to convert Date to ISO string for storage
const dateToISO = (date: Date | null): string | null =>
  date ? date.toISOString() : null

// Helper to convert ISO string back to Date
const isoToDate = (iso: string | null): Date | null =>
  iso ? new Date(iso) : null

const initialState: EngagementsState = {
  tabs: {
    range: Object.values(EngagementsTab).reduce((acc, key) => {
      // Default 120 days for leads, engagements, dreamRate; 30 days for activity
      if (
        key === EngagementsTab.leads ||
        key === EngagementsTab.engagements ||
        key === EngagementsTab.dreamRate
      ) {
        acc[key] = {
          range: "custom",
          customStartDate: dateToISO(oneHundredTwentyDaysAgo),
          customEndDate: dateToISO(now)
        }
      } else if (key === EngagementsTab.activity) {
        acc[key] = {
          range: "last_30",
          customStartDate: null,
          customEndDate: null
        }
      } else {
        acc[key] = {
          range: "last_7",
          customStartDate: null,
          customEndDate: null
        }
      }
      return acc
    }, {} as TabRangeState)
  },
  counts: {
    engagements: null,
    leads: null
  }
}

const engagementsSlice = createSlice({
  name: engagementsSliceName,
  initialState,
  reducers: {
    updateRange: (
      state,
      action: PayloadAction<{
        tabKey: EngagementsTab
        range: DateRangePreset
        customStartDate?: Date | null
        customEndDate?: Date | null
      }>
    ) => {
      const { tabKey, range, customStartDate, customEndDate } = action.payload
      state.tabs.range[tabKey] = {
        range,
        customStartDate: dateToISO(customStartDate || null),
        customEndDate: dateToISO(customEndDate || null)
      }
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      subscriptionsApi.endpoints.fetchSubscriptionLogs.matchFulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.counts.engagements = action.payload?.length ?? 0
      }
    )
    builder.addMatcher(
      leadsApi.endpoints.fetchLeadLogs.matchFulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.counts.leads = action.payload?.length ?? 0
      }
    )
  }
})

const { reducer, actions } = engagementsSlice

export const { updateRange } = actions

// Base selector for the raw range state
const selectRangeState = (state: RootState) => state.engagements.tabs.range

// Memoized selector that converts ISO strings to Date objects
export const selectTabRange = createSelector(
  [selectRangeState, (_: RootState, tabKey: EngagementsTab) => tabKey],
  (rangeState, tabKey): TabDateFilter => {
    const state = rangeState[tabKey]
    return {
      ...state,
      customStartDate: isoToDate(state.customStartDate),
      customEndDate: isoToDate(state.customEndDate)
    }
  }
)

export const selectHasEngagements = (state: RootState) =>
  (state.engagements.counts.engagements ?? 0) > 0

export const selectHasLeads = (state: RootState) =>
  (state.engagements.counts.leads ?? 0) > 0

export const engagementsReducer = reducer
